import {
  markUnexpectedFailureHandled,
  presentUnexpectedFailure,
  UNEXPECTED_FAILURE_USER_MESSAGE,
} from '../sentry/presentUnexpectedFailure';
import {
  classifyInvokeFailure,
  messageFromInvoke,
  type InvokeErr,
  type InvokeFailureKind,
} from './invokeErrorUtils';
import { supabase } from './supabaseClient';

export type InvokeEdgePresentContext = {
  module: string;
  op: string;
  title?: string;
  userMessage?: string;
  fallbackMessage?: string;
};

export type InvokeEdgeOrPresentResult<T> = {
  data: T | null;
  error: InvokeErr;
  kind: InvokeFailureKind;
  /** true cuando ya corrió toast + Sentry (`user_visible`). */
  presented: boolean;
};

type InvokeOptions = {
  body?: Record<string, unknown> | string;
  headers?: Record<string, string>;
};

/**
 * Wrapper de `supabase.functions.invoke`: en fallos **inesperados** llama
 * `presentUnexpectedFailure` (toast + tag `user_visible`). Auth / offline /
 * permiso / `body.error` de negocio se devuelven sin presentar.
 *
 * El Error queda marcado con `markUnexpectedFailureHandled` — en callers usá
 * `isUnexpectedFailureHandled` para no toastear de nuevo.
 */
export async function invokeEdgeOrPresent<T = unknown>(
  functionName: string,
  invokeOptions: InvokeOptions,
  ctx: InvokeEdgePresentContext,
): Promise<InvokeEdgeOrPresentResult<T>> {
  let data: T | null = null;
  let error: InvokeErr = null;

  try {
    const result = await supabase.functions.invoke(functionName, invokeOptions);
    data = (result.data as T) ?? null;
    error = (result.error as InvokeErr) ?? null;
  } catch (e) {
    error = e instanceof Error ? e : new Error(String(e));
  }

  // VUELTO-NEGOCIO-1Z: FunctionsHttpError deja el body en error.context (Response).
  // Si data es null pero el Response tiene body, lo leemos para que classifyInvokeFailure
  // pueda extraer el error de negocio en vez de clasificarlo como 'unexpected'.
  if (!data && error?.context && typeof (error.context as Response).text === 'function') {
    try {
      const bodyText = await (error.context as Response).text();
      if (bodyText) {
        try {
          data = JSON.parse(bodyText) as T;
        } catch {
          /* not JSON — leave data null */
        }
      }
    } catch {
      /* body already consumed — leave data null */
    }
  }

  const kind = classifyInvokeFailure(data, error);
  if (kind === 'ok') {
    return { data, error: null, kind, presented: false };
  }

  if (kind === 'unexpected') {
    const fail =
      error instanceof Error
        ? error
        : new Error(messageFromInvoke(data, error, ctx.fallbackMessage ?? UNEXPECTED_FAILURE_USER_MESSAGE));
    presentUnexpectedFailure({
      module: ctx.module,
      op: ctx.op,
      error: fail,
      title: ctx.title,
      userMessage: ctx.userMessage,
    });
    markUnexpectedFailureHandled(fail);
    return { data, error: fail, kind, presented: true };
  }

  return { data, error, kind, presented: false };
}
